'use client';

import { EventClickElement } from '@tuic/tracking';
import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import {
  Button,
  Skeleton,
  SkeletonTextLines
} from '../../../core-components';
import { convertCtaToButtonProps, type Cta } from '../../../utils/cta';
import { MeinSchiffText } from '../MeinSchiffText';

import { ContentCardImage } from './ContentCardImage';
import { ContentCardWrapper } from './ContentCardWrapper';

import type {
  TagProps} from '../../../core-components';
import type { I18NFromComponent } from '../../../utils';
import type { ClickTrackingProps } from '../../../utils';
import type { ImageProps } from '../Image';
import type { MediaCarousel } from '../MediaCarousel';
import type { DyTrackingComponentProps } from '@tuic/lib/src/utils/create-dy-engagement-tracking-props';
import type { FC, ReactNode} from 'react';

type I18N = I18NFromComponent<typeof MediaCarousel>;

export type ContentCardProps = {
  fallback?: boolean;
  image?: ImageProps | ImageProps[];
  overline?: ReactNode;
  title?: ReactNode;
  text?: ReactNode;
  tag?: TagProps;
  cta?: Cta;
  customCtaComponent?: ReactNode;
  useNativeLink?: boolean;
  i18n?: I18N;
  wrap?: ReactNode;
  forceHorizontalOnLarge?: boolean;
  imageSizeOnDesktop?: 'small' | 'medium' | 'large';
  imageOnRight?: boolean;
} & ClickTrackingProps &
  DyTrackingComponentProps;

export const ContentCard: FC<ContentCardProps> = ({
  fallback,
  image,
  overline,
  title,
  text,
  cta,
  useNativeLink,
  tag,
  i18n,
  wrap,
  forceHorizontalOnLarge,
  imageSizeOnDesktop,
  imageOnRight,
  tracking,
  dyTracking,
  customCtaComponent,
  ...rest
}) => {
  const ctaButtonProps = useMemo(
    () => convertCtaToButtonProps(cta, useNativeLink),
    [cta],
  );

  const ctaComponent =
    customCtaComponent ??
    (ctaButtonProps && (
      <Button
        variant={ctaButtonProps.variant || 'primary'}
        className="w-full @lg:w-auto"
        data-track-click-element={EventClickElement.Card}
        data-track-click-label={`button.${title}`}
        {...tracking}
        {...dyTracking}
        {...ctaButtonProps}
      />
    ));

  const normalizedImages = Array.isArray(image) ? image : image ? [image] : [];

  const imageNode = normalizedImages.length > 0 && (
    <ContentCardImage
      images={normalizedImages}
      title={title}
      cta={cta}
      tag={tag}
      fallback={fallback}
      forceHorizontalOnLarge={forceHorizontalOnLarge}
      i18n={i18n}
      dyTracking={dyTracking}
    />
  );

  const content = (
    <div
      {...rest}
      className={twMerge(
        'flex h-full flex-col @2xl:flex-row overflow-hidden',
        'rounded-sm bg-white text-marine-high-emphasis',
        forceHorizontalOnLarge && 'lg:flex-row',
      )}
    >
      {(fallback || imageNode) && (
        <div
          className={twMerge(
            'w-full aspect-4/3 @2xl:aspect-auto @2xl:min-h-[430px]',
            imageSizeOnDesktop === 'large' && '@2xl:max-w-[60%]',
            imageSizeOnDesktop === 'large' && imageOnRight && '@2xl:order-last',
            imageSizeOnDesktop === 'medium' && '@2xl:max-w-[50%]',
            imageSizeOnDesktop === 'medium' &&
              imageOnRight &&
              '@2xl:order-last',
            (!imageSizeOnDesktop || imageSizeOnDesktop === 'small') &&
              '@2xl:max-w-[30%] @2xl:min-h-[256px]',
            (!imageSizeOnDesktop || imageSizeOnDesktop === 'small') &&
              imageOnRight &&
              '@2xl:order-last',
            forceHorizontalOnLarge &&
              'lg:aspect-auto lg:max-w-[30%] lg:min-h-[256px]',
          )}
        >
          <div className="relative h-full overflow-hidden">
            {fallback && <Skeleton className="absolute inset-0 rounded-none" />}
            {!fallback && imageNode}
          </div>
        </div>
      )}
      <div
        className={twMerge(
          'flex flex-1 flex-col justify-between @2xl:justify-center',
          'p-6 @md:p-8 @2xl:px-12 @2xl:py-16 space-y-4 @2xl:space-y-6',
          forceHorizontalOnLarge && 'lg:px-12 lg:py-16',
        )}
      >
        <MeinSchiffText>
          <div className="space-y-4">
            {fallback && (
              <div className="space-y-2">
                <div className="py-2 pt-1.5">
                  <Skeleton className="h-2 w-1/4" />
                </div>

                <div className="py-1.5">
                  <Skeleton className="h-4 w-3/4" />
                </div>
              </div>
            )}

            {!fallback && (overline || title) && (
              <div className="space-y-2">
                {overline && (
                  <div className="text-base font-normal">{overline}</div>
                )}

                {title && <div className="headline-md">{title}</div>}
              </div>
            )}

            {fallback && (
              <SkeletonTextLines
                number={
                  imageSizeOnDesktop === 'large'
                    ? 5
                    : imageSizeOnDesktop === 'medium'
                      ? 4
                      : 3
                }
                className="h-2"
                wrapperClassName="py-[.9rem]"
              />
            )}

            {!fallback && text && <div className="text-lg">{text}</div>}
          </div>
        </MeinSchiffText>
        {(fallback || ctaComponent) && (
          <div className="flex flex-col space-y-4 @lg:justify-end lg:items-start">
            {fallback && <Skeleton className="h-12 w-full @lg:w-[250px]" />}
            {!fallback && ctaComponent}
          </div>
        )}
      </div>
    </div>
  );

  return <ContentCardWrapper wrap={wrap}>{content}</ContentCardWrapper>;
};
